import { PaymentInfo, PaymentPlan } from "@vixi/types";

/**
 * Checks if the user is on the Free plan
 * @param paymentInfo - The PaymentInfo object from backend
 * @returns true if user is on Free plan, false otherwise
 */
export function isFreePlan(paymentInfo: PaymentInfo | undefined): boolean {
  console.log("🔍 [planCheck] Checking Free Plan:", {
    paymentInfo,
    hasPlans: !!paymentInfo?.plans,
    plansCount: paymentInfo?.plans?.length,
  });

  if (!paymentInfo?.plans) {
    console.log("❌ [planCheck] No paymentInfo or plans array");
    return false;
  }

  // Log all plans with their details
  console.log("🔍 [planCheck] All Plans Details:", 
    paymentInfo.plans.map((plan: PaymentPlan, index: number) => ({
      index,
      planType: plan.planType,
      inPlan: plan.inPlan,
      monthlyPrice: plan.monthlyPrice,
      yearlyPrice: plan.yearlyPrice,
      title: plan.title,
    }))
  );

  const currentPlan = paymentInfo.plans.find((plan: PaymentPlan) => plan.inPlan === true);
  
  console.log("🔍 [planCheck] Current Plan (inPlan === true):", {
    currentPlan,
    planType: currentPlan?.planType,
    monthlyPrice: currentPlan?.monthlyPrice,
    yearlyPrice: currentPlan?.yearlyPrice,
    title: currentPlan?.title,
    inPlan: currentPlan?.inPlan,
  });

  if (!currentPlan) {
    console.log("❌ [planCheck] No current plan found (inPlan === true)");
    console.log("🔍 [planCheck] Trying alternative: Find plan with planType === 0 (Free)");
    
    // Alternative: Find Free plan by planType id (0 = Free)
    const freePlan = paymentInfo.plans.find((plan: PaymentPlan) => plan.planType === 0);
    if (freePlan) {
      console.log("🔍 [planCheck] Found Free plan by planType:", freePlan);
      // If we can't find current plan by inPlan, check if Free plan has inPlan === true
      // or check if all other plans have inPlan === false
      const hasOtherActivePlans = paymentInfo.plans.some(
        (plan: PaymentPlan) => plan.planType !== 0 && plan.inPlan === true
      );
      
      if (!hasOtherActivePlans) {
        console.log("✅ [planCheck] No other active plans found, assuming Free plan");
        return true;
      }
    }
    
    return false;
  }

  // Free plan has both monthlyPrice and yearlyPrice as 0, OR planType === 0
  const isFree = 
    (currentPlan.monthlyPrice === 0 && currentPlan.yearlyPrice === 0) ||
    currentPlan.planType === 0;
    
  console.log("✅ [planCheck] Is Free Plan:", isFree, {
    monthlyPrice: currentPlan.monthlyPrice,
    yearlyPrice: currentPlan.yearlyPrice,
    planType: currentPlan.planType,
  });
  
  return isFree;
}

/**
 * Checks if user should see the upgrade popup
 * (User is on Free plan AND has already generated one course - regardless of status)
 * @param paymentInfo - The PaymentInfo object from backend
 * @param allCoursesCount - Number of ALL courses (Published, In Review, Draft)
 * @returns true if popup should be shown, false otherwise
 */
export function shouldShowUpgradePopup(
  paymentInfo: PaymentInfo | undefined,
  allCoursesCount: number | undefined,
): boolean {
  console.log("🔍 [planCheck] Checking if should show upgrade popup:", {
    paymentInfo,
    allCoursesCount,
    typeOfAllCoursesCount: typeof allCoursesCount,
  });

  const isFree = isFreePlan(paymentInfo);
  console.log("🔍 [planCheck] Is Free Plan:", isFree);

  if (!isFree) {
    console.log("❌ [planCheck] User is not on Free plan, popup will not show");
    return false;
  }

  // Show popup if user has generated exactly one course (regardless of status)
  const shouldShow = allCoursesCount === 1;
  console.log("✅ [planCheck] Should show popup:", shouldShow, {
    allCoursesCount,
    condition: "allCoursesCount === 1",
  });

  return shouldShow;
}

/**
 * Checks if user should see the upgrade popup for avatars
 * (User is on Free plan AND has already generated at least one avatar)
 * @param paymentInfo - The PaymentInfo object from backend
 * @param allAvatarsCount - Number of ALL avatars
 * @returns true if popup should be shown, false otherwise
 */
export function shouldShowAvatarUpgradePopup(
  paymentInfo: PaymentInfo | undefined,
  allAvatarsCount: number | undefined,
): boolean {
  console.log("🔍 [planCheck] Checking if should show avatar upgrade popup:", {
    paymentInfo,
    allAvatarsCount,
    typeOfAllAvatarsCount: typeof allAvatarsCount,
  });

  const isFree = isFreePlan(paymentInfo);
  console.log("🔍 [planCheck] Is Free Plan:", isFree);

  if (!isFree) {
    console.log("❌ [planCheck] User is not on Free plan, avatar popup will not show");
    return false;
  }

  // Show popup if user has generated at least one avatar (>= 1)
  // This means they've already used their free avatar and trying to create another one
  const shouldShow = (allAvatarsCount ?? 0) >= 1;
  console.log("✅ [planCheck] Should show avatar popup:", shouldShow, {
    allAvatarsCount,
    condition: "allAvatarsCount >= 1",
  });

  return shouldShow;
}
